import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { CollaborationsData } from "../../types/collaborations/CollaborationTypes"
import { getErrorMessage, isFresh } from "../../utils/helpers";
import { fetchCollaborations as fetchCollaborationsAPI } from "../../services/api/collaboration";
import { apiConfigs } from "../../constants/apiConfigs";

export interface CollaborationSlice extends CollaborationsData {
    page: number,
    loading: boolean,
    last_fetched_at: number | null,
    has_more: boolean,
    expired: boolean
}

export const initialState: CollaborationSlice = {
    count: 0,
    items: [],
    loading: false,
    last_fetched_at: null,
    page: 1,
    has_more: true,
    expired: true
}


const STALE_TIME = apiConfigs.FETCH_COLLABORATIONS.staleTime;

export const fetchCollaborations = createAsyncThunk(
    'collaborations/fetchCollaborations',
    async ({ page, force = false }: { page: number; force?: boolean }, { getState, rejectWithValue }) => {
        const state = getState() as { collaborations: CollaborationSlice };
        if (page === 1 && !force && isFresh(state.collaborations.last_fetched_at, STALE_TIME) && !state.collaborations.expired) {
            return rejectWithValue("Using cached data");
        }
        try {
            const collaborations = await fetchCollaborationsAPI(page);
            return { collaborations, page };
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, 'Failed to fetch collaborations'));
        }
    }
);

const collaborationSlice = createSlice({
    name: 'collaborations',
    initialState,
    reducers: {
        setCollaborations: (state, data) => {
            state.items = data.payload;
        },
        markExpired: (state) => {
            state.expired = true
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCollaborations.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchCollaborations.fulfilled, (state, action) => {
                state.items = action.payload.page === 1 ?
                    action.payload.collaborations.items :
                    [...state.items, ...action.payload.collaborations.items];

                state.count = action.payload.collaborations.count;
                state.page = action.payload.page;
                state.loading = false;
                state.has_more = action.payload.collaborations.count > state.items.length;
                state.last_fetched_at = Date.now();
                state.expired = false;
            })
            .addCase(fetchCollaborations.rejected, (state, action) => {
                state.loading = false;
            })
    }
})

export const { setCollaborations, markExpired } = collaborationSlice.actions;

export default collaborationSlice.reducer;